import { useQuery, UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import fetchTicket from './useTicket.request';
import { UseTicketResponse } from './useTicket.interface';

import { ApiResponse } from 'modules/IMC/MessageCenter/components/MessageCenterContent/MessageList/MessageList.interface';

export default function useTicket(ticketId: number, options?: Omit<UseQueryOptions<UseTicketResponse>, 'queryKey'>) {
    const queryClient = useQueryClient();

    // Check if we can find the ticket in any of the search results cache
    const ticketFromCache = useMemo(() => {
        // Get all query data that matches the messageCenterTickets pattern
        const queryCache = queryClient.getQueryCache();
        const queries = queryCache.findAll({ queryKey: ['messageCenterTickets'] });

        // Find the ticket in any of the cached search results
        const foundTicket = queries
            .map((query) => query.state.data as ApiResponse | undefined)
            .filter((data): data is ApiResponse => !!data?.results)
            .flatMap((data) => data.results)
            .find((ticket) => ticket.id === ticketId);

        if (foundTicket) {
            // Transform the ticket data to match UseTicketResponse interface
            return {
                ticket: foundTicket,
                last_audits: foundTicket.last_audit ? [foundTicket.last_audit] : [],
            } as UseTicketResponse;
        }

        return null;
    }, [queryClient, ticketId]);

    return useQuery({
        queryKey: ['zdTicket', ticketId],
        queryFn: async () => {
            // First try to get from cache
            if (ticketFromCache) {
                return ticketFromCache;
            }
            // Fall back to API call
            return fetchTicket(ticketId);
        },
        staleTime: Infinity,
        ...options,
    });
}
